/**
 * "Ding" de notificação sintetizado na WebAudio API — SEM asset binário: dois
 * osciladores senoidais curtos (A5→D6) com envelope de ganho, ~200ms no total.
 *
 * ## Autoplay
 * Navegador nenhum deixa criar/retomar um AudioContext fora de um gesto do
 * usuário: um `new AudioContext()` no load nasce `suspended` e todo `start()`
 * fica mudo (Chrome ainda loga warning no console). Por isso:
 *
 * 1. `installNotificationSoundPriming()` (chamado uma vez pelo AppLayout)
 *    registra listeners `once` de pointerdown/keydown/touchstart no documento e
 *    cria+resume o contexto DENTRO desse gesto — na prática o login, um clique
 *    no menu ou qualquer navegação já destrava o áudio;
 * 2. `playNotificationDing()` é no-op silencioso enquanto não houver contexto
 *    destravado (nunca lança, nunca gera unhandled rejection);
 * 3. o contexto é criado UMA vez e reusado (browsers limitam contextos por
 *    aba); se ele voltar a `suspended` (aba em background por muito tempo,
 *    política do Safari), tentamos `resume()` antes de agendar as notas.
 *
 * Anti-tempestade: no máximo um ding a cada MIN_INTERVAL_MS — uma automação que
 * dispara 10 eventos de uma vez toca UM som, não dez.
 */

const MIN_INTERVAL_MS = 3_000;

type AudioContextCtor = typeof AudioContext;

let ctx: AudioContext | null = null;
let lastPlayedAt = 0;
let primingInstalled = false;

function audioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  // cast largo: `webkitAudioContext` (Safari antigo) não existe no lib.dom
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/**
 * Cria (ou retoma) o contexto. DEVE ser chamado dentro de um gesto do usuário
 * na primeira vez; fora dele o contexto nasce suspenso e o som não sai.
 */
export function primeNotificationSound(): void {
  const Ctor = audioContextCtor();
  if (!Ctor) return;
  try {
    ctx ??= new Ctor();
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
  } catch {
    ctx = null; // navegador sem áudio disponível: seguimos mudos
  }
}

/**
 * Liga o priming no primeiro gesto do usuário nesta aba. Idempotente (o
 * AppLayout monta uma vez, mas StrictMode monta duas). Devolve o cleanup.
 */
export function installNotificationSoundPriming(): () => void {
  if (primingInstalled || typeof document === 'undefined') return () => undefined;
  primingInstalled = true;
  const events = ['pointerdown', 'keydown', 'touchstart'] as const;
  const onGesture = () => {
    primeNotificationSound();
    remove();
  };
  const remove = () => {
    for (const type of events) document.removeEventListener(type, onGesture);
  };
  for (const type of events) document.addEventListener(type, onGesture, { once: true });
  return () => {
    // Não zera `primingInstalled`: o contexto (se já criado) segue vivo e o
    // priming não precisa ser reinstalado no remount.
    remove();
  };
}

function playNote(context: AudioContext, freq: number, at: number, duration: number, peak: number) {
  const osc = context.createOscillator();
  const gain = context.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, at);
  // Envelope exponencial (não pode passar por zero — daí o 0.0001): ataque
  // curtinho e decaimento suave, sem o "clique" do corte seco.
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(peak, at + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
  osc.connect(gain);
  gain.connect(context.destination);
  osc.start(at);
  osc.stop(at + duration + 0.02);
  osc.onended = () => {
    osc.disconnect();
    gain.disconnect();
  };
}

/**
 * Toca o ding. Devolve false quando nada tocou (áudio ainda travado pelo
 * autoplay, navegador sem WebAudio ou debounce em vigor).
 */
export function playNotificationDing(): boolean {
  const now = Date.now();
  if (now - lastPlayedAt < MIN_INTERVAL_MS) return false;
  if (!ctx) return false; // sem gesto ainda: silêncio (não tenta criar aqui)
  if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
  if (ctx.state !== 'running') return false;
  lastPlayedAt = now;
  try {
    const t0 = ctx.currentTime + 0.01;
    playNote(ctx, 880, t0, 0.09, 0.14); // A5
    playNote(ctx, 1174.66, t0 + 0.085, 0.13, 0.11); // D6
    return true;
  } catch {
    return false;
  }
}

/** Só para os testes manuais/preview do toggle nas preferências. */
export function previewNotificationDing(): void {
  primeNotificationSound(); // estamos dentro do clique do usuário
  lastPlayedAt = 0; // preview nunca é engolido pelo debounce
  playNotificationDing();
}
